/**
 * Programs sagas
 */
import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import { httpRequest } from '../../services/http-service';
import { endPoints } from '../../services/endpoints';
import {
  fetchPrograms,
  fetchProgram,
  createProgram,
  updateProgramAction,
  deleteProgram,
  setPrograms,
  setCurrentProgram,
  addProgram,
  updateProgram,
  removeProgram,
  setLoading,
  setError,
} from './programsSlice';
import { presentLoader, dismissLoader } from '../../store/slices/commonSlice';

/**
 * Fetch all programs saga
 */
function* fetchProgramsSaga(action: ReturnType<typeof fetchPrograms>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const params: { activeOnly?: boolean; page?: number; limit?: number } = {
      activeOnly: false,
      page: 1,
      limit: 10,
    };

    if (action.payload) {
      if (action.payload.page !== undefined) params.page = action.payload.page;
      if (action.payload.limit !== undefined) params.limit = action.payload.limit;
      if (action.payload.activeOnly !== undefined) params.activeOnly = action.payload.activeOnly;
    }

    const response = yield call(httpRequest.get, endPoints.programs.getAll, {
      params,
    });

    if (response?.success) {
      const responseData = response.data || response;
      // Backend returns data wrapped in { program, enrollment, progress, isEnrolled }
      // Extract just the program objects for admin dashboard, preserving categoryDetails
      if (Array.isArray(responseData)) {
        const programs = responseData.map((item: any) => {
          const program = item.program || item;
          // Ensure categoryDetails is preserved if present
          if (item.program && item.program.categoryDetails) {
            program.categoryDetails = item.program.categoryDetails;
          }
          return program;
        });
        yield put(setPrograms(programs));
      } else if (responseData.data) {
        const programs = (responseData.data || []).map((item: any) => {
          const program = item.program || item;
          // Ensure categoryDetails is preserved if present
          if (item.program && item.program.categoryDetails) {
            program.categoryDetails = item.program.categoryDetails;
          }
          return program;
        });
        yield put(setPrograms({ data: programs, ...responseData }));
      } else {
        yield put(setPrograms([]));
      }
    } else {
      toast.error('Failed to fetch programs');
      yield put(setError('Failed to fetch programs'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch programs';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Fetch single program saga
 */
function* fetchProgramSaga(action: ReturnType<typeof fetchProgram>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.programs.getById(action.payload));

    if (response?.success) {
      // Backend returns data wrapped in { program, enrollment, progress, isEnrolled }
      // Extract just the program object for admin dashboard, preserving categoryDetails
      const data = response.data || response;
      const program = data.program || data;
      // Ensure categoryDetails is preserved if present
      if (data.program && data.program.categoryDetails) {
        program.categoryDetails = data.program.categoryDetails;
      }
      yield put(setCurrentProgram(program));
    } else {
      toast.error('Failed to fetch program');
      yield put(setError('Failed to fetch program'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch program';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Create program saga
 */
function* createProgramSaga(action: ReturnType<typeof createProgram>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.post, endPoints.programs.create, action.payload);

    if (response?.success) {
      const newProgram = response.data || response;
      yield put(addProgram(newProgram));
    } else {
      toast.error('Failed to create program');
      yield put(setError('Failed to create program'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to create program';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Update program saga
 */
function* updateProgramSaga(action: ReturnType<typeof updateProgramAction>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.put,
      endPoints.programs.update(action.payload.id),
      action.payload.data
    );

    if (response?.success) {
      const updatedProgram = { ...response.data, _id: action.payload.id };
      yield put(updateProgram(updatedProgram));
    } else {
      toast.error('Failed to update program');
      yield put(setError('Failed to update program'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to update program';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Delete program saga
 */
function* deleteProgramSaga(action: ReturnType<typeof deleteProgram>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.delete, endPoints.programs.delete(action.payload));

    if (response?.success || response) {
      yield put(removeProgram(action.payload));
    } else {
      toast.error('Failed to delete program');
      yield put(setError('Failed to delete program'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to delete program';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

export function* programsSagas(): Generator<any, void, any> {
  yield takeLatest(fetchPrograms, fetchProgramsSaga);
  yield takeLatest(fetchProgram, fetchProgramSaga);
  yield takeLatest(createProgram, createProgramSaga);
  yield takeLatest(updateProgramAction, updateProgramSaga);
  yield takeLatest(deleteProgram, deleteProgramSaga);
}

